import type { FormikValues } from 'formik';
import { useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import Auth from '../../../api/auth';
import { FormModal } from '../../../components/Formik';
import { filterDataUpdates } from '../../../helpers/objects';
import useApiQuery from '../../../hooks/useApiQuery';

import type { APIHookArg } from '@/types';
import type { FormikFormField } from '@/types/formTypes';
import type { Organization } from '@/types/organization';
import type { OnboardingStatus, User, UserResources } from '@/types/user';

// A user in either of these onboarding states has nothing valid to edit
// here yet (no completed onboarding, or no org assigned) - map to the
// notice this form should show instead of the usual editable fields.
const ONBOARDING_NOTICE_MESSAGE_ID: Partial<Record<OnboardingStatus, string>> = {
  onboarding_required: 'userOnboardingRequired',
  pending_assignment: 'userPendingOrgAssignment',
};

interface EditUserFormModalProps {
  onSubmit: () => void;
  onCancel: () => void;
  user: User;
  roles: Record<string, string>;
  canEditOrg: boolean;
}

const EditUserFormModal: React.FC<EditUserFormModalProps> = ({
  onSubmit,
  onCancel,
  user,
  roles,
  canEditOrg,
}) => {
  const intl = useIntl();
  const originalOrgId = useRef<string>(user.organization?._id ?? '');

  const [confirmSave, setConfirmSave] = useState<boolean>(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(user.organization?._id ?? '');

  const { data: userResources = {}, makeApiCall: fetchUserResources } = useApiQuery<UserResources>(
    Auth.fetchV1UserResources as unknown as (args: unknown) => Promise<UserResources>
  );
  const {
    data: organizations = [],
    error: fetchError,
    makeApiCall: fetchOrganizations,
  } = useApiQuery<Organization[]>(
    Auth.fetchV1Organizations as unknown as (args: unknown) => Promise<Organization[]>
  );

  const { makeApiCall: updateUserCall } = useApiQuery(Auth.updateV2User as unknown as APIHookArg, {
    successMessage: 'userUpdated',
  });

  useEffect(() => {
    const getOrganizations = async (): Promise<void> => {
      await fetchOrganizations();
    };

    const getUserResources = async (): Promise<void> => {
      if (!userResources.products?.length) {
        await fetchUserResources(user.uuid);
      }
    };

    if (canEditOrg) getOrganizations();
    getUserResources();
  }, [
    canEditOrg,
    fetchOrganizations,
    fetchUserResources,
    user.uuid,
    userResources.products?.length,
  ]);

  useEffect(() => {
    const orgChanged = selectedOrgId !== originalOrgId.current;
    const hasProducts = (userResources.products?.length ?? 0) > 0;
    const shouldConfirmSave = orgChanged && hasProducts;

    setConfirmSave(shouldConfirmSave);
  }, [selectedOrgId, userResources]);

  const onboardingNoticeMessageId = user.onboardingStatus
    ? ONBOARDING_NOTICE_MESSAGE_ID[user.onboardingStatus]
    : undefined;
  const isBlockedOnboardingStatus = Boolean(onboardingNoticeMessageId);

  const fields: FormikFormField[] = [
    ...(onboardingNoticeMessageId
      ? [
          {
            fieldType: 'inlineNotification',
            name: 'onboardingNotice',
            mode: 'warning',
            title: intl.formatMessage({ id: onboardingNoticeMessageId }),
            style: { marginBottom: '1rem' },
          },
        ]
      : []),
    {
      fieldType: 'select',
      name: 'roles',
      label: intl.formatMessage({ id: 'role' }),
      menuItems: Object.keys(roles).map(key => ({
        value: key,
        label: roles[key] ?? '',
      })),
      disabled: true,
      helperText: intl.formatMessage({ id: 'userRoleManagedExternally' }),
      mode: 'informative',
    },
    ...(canEditOrg && organizations.length > 0
      ? [
          {
            fieldType: 'select',
            name: 'organizationId',
            label: intl.formatMessage({ id: 'organization' }),
            menuItems: organizations.map(({ _id, enterpriseName }) => ({
              value: _id ?? '',
              label: enterpriseName,
            })),
            disabled: isBlockedOnboardingStatus,
            onChange: (e: string) => {
              setSelectedOrgId(e);
            },
          },
        ]
      : []),
    ...(confirmSave
      ? [
          {
            fieldType: 'productUnassignment',
            name: 'productUnassignment',
            resources: userResources,
          },
        ]
      : []),
  ];

  const initialValues = {
    roles: user.role,
    organizationId: user.organization?._id ?? '',
  };

  const validationSchema = Yup.object().shape({
    roles: Yup.string().required(intl.formatMessage({ id: 'formRequiredError' })),
    organizationId: Yup.string().required(intl.formatMessage({ id: 'formRequiredError' })),
  });

  const updateUser = async (formValues: FormikValues): Promise<void> => {
    onCancel();

    const updatedUser = filterDataUpdates(user as unknown as Record<string, unknown>, formValues);

    if (updatedUser.roles) {
      if (typeof updatedUser.roles === 'string') {
        updatedUser.roles = updatedUser.roles.split(',');
      }
    }

    const data = await updateUserCall({
      ...updatedUser,
      uuid: user.uuid,
    });

    if (data) {
      onSubmit();
    }
  };

  return (
    <FormModal
      title={intl.formatMessage({ id: 'editUserTitle' }, { email: user.email })}
      fields={fields}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => {
        if (!isBlockedOnboardingStatus) {
          updateUser(values as FormikValues).catch(console.error);
        }
      }}
      onCancel={onCancel}
      submitLabel={intl.formatMessage({ id: 'save' })}
      error={fetchError}
    />
  );
};

export default EditUserFormModal;
